/**
 * Copyright © 2020-2026 Delicious AI, LLC
 */

import React, { useContext, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import {
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from 'firebase/auth'
import UserContext from '../context/UserContext'
import { auth } from '../api/Firebase'
import { ReactComponent as BlackLogo } from '@/vendor/logo-black.svg'
import { ReactComponent as WhiteLogo } from '@/vendor/logo-white.svg'
import useT from '@/i18n/useT'
import useTheme from '@/hooks/useTheme'
import { THEMES } from '@/constants'

const LoginContainer: React.FunctionComponent = () => {
  const { user, error: backendError } = useContext(UserContext)
  const { state } = useLocation()
  const { theme } = useTheme()
  const { t } = useT()
  const isDark = theme === THEMES.DARK

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [resetSent, setResetSent] = useState(false)

  if (user) {
    return <Navigate to={{ pathname: '/' }} state={state} />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await signInWithEmailAndPassword(auth, email, password)
    } catch (err: unknown) {
      const firebaseError = err as { code?: string; message?: string }
      const code = firebaseError.code ?? ''
      const errorMessages: Record<string, string> = {
        'auth/user-not-found': t(
          'login.errors.invalidCredentials',
          'Invalid email or password.',
        ),
        'auth/wrong-password': t(
          'login.errors.invalidCredentials',
          'Invalid email or password.',
        ),
        'auth/invalid-credential': t(
          'login.errors.invalidCredentials',
          'Invalid email or password.',
        ),
        'auth/too-many-requests': t(
          'login.errors.tooManyRequests',
          'Too many attempts. Please try again later.',
        ),
      }
      setError(
        errorMessages[code] ??
          firebaseError.message ??
          t('login.errors.loginFailed', 'Login failed'),
      )
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async () => {
    if (loading) {
      return
    }
    if (!email) {
      setError(
        t('login.errors.enterEmailFirst', 'Enter your email above first.'),
      )
      return
    }
    setError(null)
    setLoading(true)
    try {
      await sendPasswordResetEmail(auth, email)
    } catch {
      // Intentionally ignore errors to prevent email enumeration
    } finally {
      setResetSent(true)
      setLoading(false)
    }
  }

  const displayError =
    error ?? (auth.currentUser && backendError ? backendError : null)

  return (
    <Stack
      height="100vh"
      justifyContent="center"
      alignItems="center"
      sx={theme => ({ backgroundColor: theme.palette.background.default })}
    >
      <Box
        sx={{ width: '100%', maxWidth: 480, px: 2, boxSizing: 'border-box' }}
      >
        <Stack spacing={4} alignItems="center">
          {isDark ? <WhiteLogo width={300} /> : <BlackLogo width={300} />}
          <Paper sx={{ p: 6, width: '100%' }}>
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                {displayError && (
                  <Typography
                    color="error"
                    variant="body2"
                    role="alert"
                    aria-live="assertive"
                  >
                    {displayError}
                  </Typography>
                )}
                <TextField
                  label={t('login.email.label', 'Email')}
                  type="email"
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value)
                    setResetSent(false)
                  }}
                  fullWidth
                  required
                  autoComplete="email"
                  autoFocus
                />
                <TextField
                  label={t('login.password.label', 'Password')}
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  fullWidth
                  required
                  autoComplete="current-password"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(s => !s)}
                            edge="end"
                            aria-label={t(
                              'login.password.toggleVisibility',
                              'Toggle password visibility',
                            )}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <Stack spacing={1} alignItems="flex-start">
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleForgotPassword}
                    disabled={loading}
                    sx={{
                      textTransform: 'none',
                      alignSelf: 'flex-start',
                      p: 0,
                    }}
                  >
                    {t('login.forgotPassword.action', 'Forgot password?')}
                  </Button>
                  {resetSent && (
                    <Typography
                      variant="body2"
                      color="success.main"
                      aria-live="polite"
                    >
                      {t(
                        'login.forgotPassword.confirmation',
                        'If an account exists for that email, password reset instructions have been sent.',
                      )}
                    </Typography>
                  )}
                </Stack>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={loading}
                >
                  {loading
                    ? t('login.submit.loading', 'Signing in...')
                    : t('login.submit.default', 'Sign in')}
                </Button>
              </Stack>
            </form>
          </Paper>
        </Stack>
      </Box>
    </Stack>
  )
}

export default LoginContainer
